import { FileActionButton } from "../file-action/file-action-controls";
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import planning from "../../services/orders/delivery-planning.service";
import { PlanningAssessment, PlanningAssessmentSelection, PlanningAssignment, PlanningOption } from "../../models/delivery-planning";
import { calendarDate, dateForCalendar, indiaToday, newRequestId } from "../../utils/delivery-planning";
import { generatePDFCustomerDelivery } from "../Driver-DeliveryDetails/CustomerDelieryReport";
import { generateProductSummaryPDF } from "../Driver-DeliveryDetails/DeliveryChallanPDF";
import "./DeliveryAssessmentPlanning.css";

type Access = { canView: boolean; canEdit: boolean; canDelete?: boolean; today: string };
type AssessmentRow = PlanningAssessment & { key: string };
const orderCountLabel = (count: number) => `${count} order${count === 1 ? "" : "s"}`;
const errorMessage = (error: unknown) => axios.isAxiosError<{ message?: string }>(error)
  ? error.response?.data?.message || "The request failed. You can retry."
  : error instanceof Error ? error.message : "The request failed.";

const DeliveryAssessmentPlanning = () => {
  const toast = useRef<Toast>(null);
  const [access, setAccess] = useState<Access | null>(null);
  const [rows, setRows] = useState<PlanningAssessment[]>([]);
  const [assessment, setAssessment] = useState<PlanningAssessment | null>(null);
  const [orders, setOrders] = useState<PlanningAssignment[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selected, setSelected] = useState<AssessmentRow[]>([]);
  const [prepared, setPrepared] = useState<PlanningAssessmentSelection[]>([]);
  const [total, setTotal] = useState(0);
  const [executives, setExecutives] = useState<PlanningOption[]>([]);
  const [vehicles, setVehicles] = useState<PlanningOption[]>([]);
  const [executiveId, setExecutiveId] = useState<number | null>(null);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [includeOverdue, setIncludeOverdue] = useState(true);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [swapOpen, setSwapOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [targetExecutive, setTargetExecutive] = useState<number | null>(null);
  const [targetVehicle, setTargetVehicle] = useState<number | null>(null);
  const [targetDate, setTargetDate] = useState<Date | null>(() => dateForCalendar(indiaToday()));
  const pendingRequest = useRef<{ key: string; id: string } | null>(null);
  const loadSequence = useRef(0);
  const ordersSequence = useRef(0);
  const working = useRef(false);
  const showError = useCallback((error: unknown) => toast.current?.show({ severity: "error", summary: "Delivery planning", detail: errorMessage(error), life: 6000 }), []);

  useEffect(() => { planning.access().then(setAccess).catch(showError); }, [showError]);
  useEffect(() => {
    if (access?.canView) {
      Promise.all([planning.executives(), planning.vehicles()])
        .then(([drivers, fleet]) => { setExecutives(drivers); setVehicles(fleet); }).catch(showError);
    }
  }, [access?.canView, showError]);

  const load = useCallback(async () => {
    if (!access?.canView) return;
    const sequence = ++loadSequence.current;
    setLoading(true);
    try {
      const result = await planning.assessments({
        fromDate: fromDate ? calendarDate(fromDate) : undefined,
        toDate: toDate ? calendarDate(toDate) : undefined,
        executiveId: executiveId ?? undefined, includeOverdue, page: page + 1, pageSize: size,
      });
      if (sequence === loadSequence.current) { setRows(result.assessments); setTotal(result.totalCount); setSelected([]); }
    } catch (error) { if (sequence === loadSequence.current) showError(error); }
    finally { if (sequence === loadSequence.current) setLoading(false); }
  }, [access?.canView, fromDate, toDate, executiveId, includeOverdue, page, size, showError]);
  useEffect(() => { void load(); }, [load]);

  const resetFilters = () => {
    if (working.current) return;
    setExecutiveId(null); setFromDate(null); setToDate(null);
    setIncludeOverdue(true); setPage(0); setSelected([]);
  };

  const requestId = (kind: string, payload: unknown) => {
    const key = JSON.stringify({ kind, payload });
    if (pendingRequest.current?.key !== key) pendingRequest.current = { key, id: newRequestId() };
    return pendingRequest.current.id;
  };
  const loadOrders = async (item: PlanningAssessment) => {
    const sequence = ++ordersSequence.current;
    setOrders([]); setOrdersLoading(true);
    try {
      const result = await planning.assessmentOrders(item.executiveId, item.deliveryDate.slice(0, 10));
      if (sequence === ordersSequence.current) {
        setOrders(result);
      }
    } catch (error) { if (sequence === ordersSequence.current) showError(error); }
    finally { if (sequence === ordersSequence.current) setOrdersLoading(false); }
  };
  const openAssessment = (item: PlanningAssessment) => {
    if (busy) return;
    setAssessment(item); void loadOrders(item);
  };
  const closeAssessment = () => {
    if (busy) return;
    ++ordersSequence.current; setAssessment(null); setOrders([]); setOrdersLoading(false);
  };

  const eligible = selected.length > 0 && selected.every(a => !a.isOverdue && a.deliveryDate.slice(0, 10) >= indiaToday());
  const canMove = !!access?.canEdit && selected.length === 1 && eligible && !busy && !loading;
  const canSwap = !!access?.canEdit && selected.length === 2 && eligible && !busy && !loading;
  const canDelete = !!access?.canDelete && selected.length === 1 && eligible && !busy && !loading;
  const prepare = async (kind: "move" | "swap" | "delete") => {
    if (working.current || !(kind === "move" ? canMove : kind === "swap" ? canSwap : canDelete)) return;
    working.current = true; setBusy(true);
    try {
      const snapshots = await Promise.all(selected.map(async item => {
        const members = await planning.assessmentOrders(item.executiveId, item.deliveryDate.slice(0, 10));
        if (new Set(members.map(a => a.orderId)).size !== item.orderCount || !members.length)
          throw new Error("An assessment changed. Refresh the list before trying again.");
        if (members.some(a => a.isOverdue || a.deliveryDate.slice(0, 10) < indiaToday()) ||
            (kind !== "swap" && members.some(a => !a.canMove)))
          throw new Error("The selected assessment contains overdue, closed or invoice/challan-linked orders and cannot be changed.");
        if (kind === "delete" && members.some(a => a.canUnassign !== true))
          throw new Error("The assessment cannot be deleted: every order must be active, open, non-overdue and free of invoices and challans.");
        return { executiveId: item.executiveId, deliveryDate: item.deliveryDate.slice(0, 10),
          assignments: members.map(a => ({ id: a.id, rowVersion: a.rowVersion })),
          ...(kind === "swap" ? { swapAssignmentIds: members.filter(a => a.canMove).map(a => a.id) } : {}) };
      }));
      if (kind === "swap" && snapshots.every(a => !a.swapAssignmentIds?.length))
        throw new Error("Neither assessment has eligible open orders to swap.");
      setPrepared(snapshots);
      if (kind === "move") {
        setTargetExecutive(selected[0].executiveId); setTargetDate(dateForCalendar(indiaToday())); setTargetVehicle(null); setMoveOpen(true);
      } else if (kind === "swap") setSwapOpen(true);
      else setDeleteOpen(true);
    } catch (error) { showError(error); }
    finally { working.current = false; setBusy(false); }
  };

  const mutate = async (action: () => Promise<unknown>, message: string) => {
    if (working.current) return;
    working.current = true; setBusy(true);
    let saved = false;
    try {
      await action(); saved = true; pendingRequest.current = null;
      setMoveOpen(false); setSwapOpen(false); setDeleteOpen(false); setSelected([]);
      toast.current?.show({ severity: "success", summary: "Saved", detail: message, life: 5000 });
      const current = await planning.access(); setAccess(current);
      if (current.canView) { await load(); if (assessment) await loadOrders(assessment); }
      else { setRows([]); setTotal(0); ++ordersSequence.current; setAssessment(null); setOrders([]); }
    } catch (error) {
      if (saved) {
        toast.current?.show({ severity: "warn", summary: "Saved", detail: "The change was saved, but refreshing failed. Refresh this page before another change.", life: 6000 });
        return;
      }
      showError(error);
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        pendingRequest.current = null; setSelected([]); setDeleteOpen(false); await load();
        if (assessment) await loadOrders(assessment);
      }
    } finally { working.current = false; setBusy(false); }
  };

  const move = () => {
    if (!canMove || prepared.length !== 1 || !targetDate || !targetExecutive || !targetVehicle || calendarDate(targetDate) < indiaToday()) return;
    const payload = { assessment: prepared[0], executiveId: targetExecutive, deliveryDate: calendarDate(targetDate), vehicleId: targetVehicle };
    return mutate(() => planning.moveAssessment({ ...payload, requestId: requestId("move-assessment", payload) }), "Assessment moved. Review the destination route order before delivery.");
  };
  const swap = () => {
    if (!canSwap || prepared.length !== 2) return;
    const payload = { assessments: prepared };
    return mutate(() => planning.swapAssessments({ ...payload, requestId: requestId("swap-assessments", payload) }),
      "Eligible open orders swapped. Locked and delivered orders stayed in place. Review the updated route order.");
  };
  const deleteAssessment = () => {
    if (!canDelete || prepared.length !== 1) return;
    const payload = { assessment: prepared[0] };
    return mutate(() => planning.unassignAssessment({ ...payload, requestId: requestId("unassign-assessment", payload) }),
      "Assessment deleted. Its orders are unassigned. Refresh Smart Map to view eligible open orders.");
  };
  const report = async (driver: number, date: string) => {
    if (working.current) return;
    working.current = true; setBusy(true);
    try {
      const records = await planning.report(driver, date);
      if (!records.length) {
        toast.current?.show({ severity: "info", summary: "Customer report", detail: "No eligible orders for this executive and date." });
      } else {
        await generatePDFCustomerDelivery(records, orderId => planning.instructions(orderId, driver, date));
      }
    } catch (error) { showError(error); }
    finally { working.current = false; setBusy(false); }
  };

  const exportPreChallan = async (item: PlanningAssessment) => {
    if (working.current || !access?.canView) return;
    working.current = true; setBusy(true);
    try {
      const data = await planning.preChallan(item.executiveId, item.deliveryDate.slice(0, 10));
      if (!data.products.lineItems.length && !data.products.categorySummaries.length) {
        toast.current?.show({ severity: "info", summary: "Export PreChallan", detail: "No products were found for this assessment." });
        return;
      }
      await generateProductSummaryPDF({
        categorySummaries: data.products.categorySummaries,
        lineItems: data.products.lineItems,
        drivername: data.products.drivername || item.executiveName,
        vesselDetails: data.vesselDetails.map(v => ({ ...v,
          vessels_5L: v.vessels_5L ?? 0, vessels_3L: v.vessels_3L ?? 0, vessels_2L: v.vessels_2L ?? 0 })),
        // PreChallans have no issued challan header; no separate POS-management access is needed.
        fetchPOS: false,
      });
    } catch (error) { showError(error); }
    finally { working.current = false; setBusy(false); }
  };

  const executiveOptions = executives.map(e => ({ ...e, label: `${e.name} (${e.phone})` }));
  const today = dateForCalendar(indiaToday());
  const dateLabel = (value: string) => dateForCalendar(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  return <div className="card">
    <Toast ref={toast} />
    <div className="flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
      <div><h3 className="mb-1">Delivery Assessment Planning</h3>
        <span className="text-600">One assessment per executive and delivery date. Open an assessment to view its orders. Dates use IST.</span></div>
      {access?.canView && <Button label="Refresh" icon="pi pi-refresh" onClick={() => { setSelected([]); void load(); if (assessment) void loadOrders(assessment); }} disabled={busy || loading} />}
    </div>
    {!access && <p>Checking planning access…</p>}
    {access && !access.canView && <p>Planning access is not enabled for your account.</p>}
    {access?.canView && <>
      <div className="surface-100 border-round p-3 flex flex-wrap gap-3 align-items-end mb-3">
        <div><label className="block mb-2" htmlFor="planning-executive">Delivery executive</label>
          <Dropdown inputId="planning-executive" value={executiveId} options={executiveOptions} optionLabel="label" optionValue="id"
            showClear filter placeholder="All executives" disabled={busy} onChange={e => { setExecutiveId(e.value); setPage(0); }} /></div>
        <div><label className="block mb-2" htmlFor="planning-from">From date</label>
          <Calendar inputId="planning-from" value={fromDate} showIcon showButtonBar dateFormat="dd/mm/yy" readOnlyInput disabled={busy}
            onChange={e => { setFromDate(e.value as Date | null); setPage(0); }} /></div>
        <div><label className="block mb-2" htmlFor="planning-to">To date</label>
          <Calendar inputId="planning-to" value={toDate} showIcon showButtonBar dateFormat="dd/mm/yy" readOnlyInput disabled={busy}
            minDate={fromDate ?? undefined} onChange={e => { setToDate(e.value as Date | null); setPage(0); }} /></div>
        <div className="flex align-items-center gap-2 pb-2"><Checkbox inputId="planning-overdue" checked={includeOverdue} disabled={busy}
          onChange={e => { setIncludeOverdue(!!e.checked); setPage(0); }} /><label htmlFor="planning-overdue">Include overdue assessments and history</label></div>
        <Button label="Reset filters" icon="pi pi-filter-slash" outlined disabled={busy} onClick={resetFilters} />
      </div>
      <div className="flex flex-wrap align-items-center gap-2 mb-3">
        {access.canEdit && <Button label={`Move selected (${selected.length})`} icon="pi pi-calendar" disabled={!canMove}
          onClick={() => void prepare("move")} />}
        {access.canEdit && <Button label="Swap two assignments" icon="pi pi-arrow-right-arrow-left" outlined
          disabled={!canSwap} onClick={() => void prepare("swap")} />}
        {access.canDelete && <Button label="Delete selected assessment" icon="pi pi-trash" severity="danger" outlined
          disabled={!canDelete} onClick={() => void prepare("delete")} />}
        {(access.canEdit || access.canDelete) && selected.length > 0 && <Button label="Clear selection" text disabled={busy} onClick={() => setSelected([])} />}
        <span className="ml-auto font-semibold">{total.toLocaleString()} assessments</span>
      </div>
      {(access.canEdit || access.canDelete) && <p>Select one assessment to move or delete, or two to swap. Overdue assessments cannot be selected.</p>}
      <p>Undelivered overdue orders without an invoice return automatically for replanning. Returned orders are included in Not delivered. Past outcomes remain unchanged after later delivery; unknown outcomes are shown separately.</p>
      <DataTable value={rows.map(a => ({ ...a, key: `${a.executiveId}|${a.deliveryDate.slice(0, 10)}` }))} dataKey="key" lazy paginator first={page * size} rows={size} totalRecords={total} rowsPerPageOptions={[25, 50, 100]}
        selection={selected} selectionMode="checkbox" showSelectAll={false}
        onSelectionChange={e => setSelected((e.value as AssessmentRow[]).slice(0, 2))}
        isDataSelectable={e => !!(access.canEdit || access.canDelete) && !e.data.isOverdue && e.data.deliveryDate.slice(0, 10) >= indiaToday()
          && !busy && !moveOpen && !swapOpen && !deleteOpen && (selected.some(a => a.key === e.data.key) || selected.length < (access.canEdit ? 2 : 1))}
        loading={loading} onRowClick={e => {
          const target = e.originalEvent.target;
          if (target instanceof Element && target.closest(".p-selection-column, .p-checkbox, button, input")) return;
          openAssessment(e.data as PlanningAssessment);
        }} rowHover emptyMessage="No assessments match these filters."
        onPage={e => { setPage(Math.floor(e.first / e.rows)); setSize(e.rows); }} responsiveLayout="scroll">
        {(access.canEdit || access.canDelete) && <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />}
        <Column field="deliveryDate" header="Delivery date" body={(a: PlanningAssessment) => <div>{dateLabel(a.deliveryDate)} {a.isOverdue && <Tag value="Overdue" severity="warning" />}</div>} />
        <Column field="executiveName" header="Delivery executive" />
        <Column field="orderCount" header="Total orders" />
        <Column header="Delivered" body={(a: PlanningAssessment) => a.deliveredCount ?? "—"} />
        <Column header="Not delivered" body={(a: PlanningAssessment) => a.notDeliveredCount ?? "—"} />
        <Column header="Pending" body={(a: PlanningAssessment) => a.pendingCount ?? "—"} />
        <Column header="Returned for replanning" body={(a: PlanningAssessment) => a.releasedCount ?? "—"} />
        <Column header="Cancelled" body={(a: PlanningAssessment) => a.cancelledCount ?? "—"} />
        <Column header="Unknown" body={(a: PlanningAssessment) => a.unknownCount ?? "—"} />
        <Column header="Reports" body={(a: PlanningAssessment) => <div className="planning-report-actions">
          <Button label="View orders" icon="pi pi-list" size="small" outlined disabled={busy}
            onClick={e => { e.stopPropagation(); openAssessment(a); }} />
          <FileActionButton action="export" icon="pi pi-download" label="Export PreChallan" severity="info" size="small" outlined disabled={busy}
            onClick={e => { e.stopPropagation(); void exportPreChallan(a); }} />
          <FileActionButton action="export" icon="pi pi-file-pdf" label="Customer report" size="small" outlined disabled={busy}
            onClick={e => { e.stopPropagation(); void report(a.executiveId, a.deliveryDate.slice(0, 10)); }} />
        </div>} />
      </DataTable>
    </>}
    <Dialog visible={!!assessment && !!access?.canView} header={assessment ? `${assessment.executiveName} — ${dateLabel(assessment.deliveryDate)}` : "Assessment orders"}
      modal style={{ width: "min(95vw, 1400px)" }} closable={!busy} onHide={closeAssessment}>
      <div className="flex flex-wrap align-items-center gap-2 mb-3">
        <span className="font-semibold">{orders.length} assigned orders</span>
        {assessment && <FileActionButton action="export" label="Customer report" icon="pi pi-file-pdf" outlined disabled={busy || ordersLoading}
          onClick={() => void report(assessment.executiveId, assessment.deliveryDate.slice(0, 10))} />}
      </div>
      <DataTable key={`${assessment?.executiveId}|${assessment?.deliveryDate}`} value={orders} dataKey="id" paginator rows={25} rowsPerPageOptions={[10, 25, 50, 100]} loading={ordersLoading}
        emptyMessage="No assigned orders for this executive and delivery date." responsiveLayout="scroll">
        <Column field="sequenceNo" header="Route position" />
        <Column field="orderId" header="Order #" />
        <Column field="customerName" header="Customer" />
        <Column field="customerPhone" header="Phone" />
        <Column field="vehicleNo" header="Vehicle" />
        <Column header="Status" body={(a: PlanningAssignment) => <span>{a.status}{!a.canMove && !a.isReleased && (!a.outcome || a.outcome === "Pending") ? " (locked)" : ""}</span>} />
        <Column field="delayedStatusName" header="Delay reason" body={(a: PlanningAssignment) => a.delayedStatusName ||
          (a.delayedStatusId === 4 ? "Delivery Time Out" : a.delayedStatusId ? "Reason unavailable" : "—")} />
        <Column field="delayRemarks" header="Remarks" body={(a: PlanningAssignment) => a.delayRemarks || "—"} />
        <Column header="Returned for replanning" body={(a: PlanningAssignment) => a.releasedUtc
          ? new Date(a.releasedUtc.endsWith("Z") ? a.releasedUtc : `${a.releasedUtc}Z`).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"} />
      </DataTable>
    </Dialog>
    <Dialog visible={deleteOpen} header="Delete selected assessment" modal style={{ width: "min(95vw, 520px)" }}
      closable={!busy} onHide={() => { if (!busy) setDeleteOpen(false); }}
      footer={<div className="flex justify-content-end gap-2">
        <Button label="Cancel" outlined disabled={busy} onClick={() => setDeleteOpen(false)} />
        <Button label="Confirm delete" icon="pi pi-trash" severity="danger" loading={busy}
          disabled={!canDelete || prepared.length !== 1} onClick={() => void deleteAssessment()} />
      </div>}>
      <p>Delete this assessment and unassign all {prepared[0]?.assignments.length ?? 0} orders? Eligible orders will appear in Smart Map after refresh. Assignment history is retained.</p>
    </Dialog>
    <Dialog visible={moveOpen} header="Move delivery assignments" modal style={{ width: "min(95vw, 480px)" }}
      onHide={() => { if (!busy) setMoveOpen(false); }} closable={!busy}
      footer={<Button label="Move assignments" icon="pi pi-check" loading={busy}
        disabled={!canMove || prepared.length !== 1 || !targetExecutive || !targetVehicle || !targetDate || calendarDate(targetDate) < indiaToday()} onClick={() => void move()} />}>
      <p>Move all {prepared[0]?.assignments.length ?? 0} orders in this assessment to the chosen executive and date. They will be appended to the destination route.</p>
      <label htmlFor="move-executive" className="block mb-2">Delivery executive</label>
      <Dropdown inputId="move-executive" className="w-full mb-3" options={executiveOptions} optionLabel="label" optionValue="id" filter
        value={targetExecutive} disabled={busy} onChange={e => setTargetExecutive(e.value)} />
      <label htmlFor="move-date" className="block mb-2">Delivery date</label>
      <Calendar inputId="move-date" className="w-full mb-3" value={targetDate} minDate={today} dateFormat="dd/mm/yy" showIcon readOnlyInput
        disabled={busy} onChange={e => setTargetDate(e.value as Date | null)} />
      <label htmlFor="move-vehicle" className="block mb-2">Vehicle</label>
      <Dropdown inputId="move-vehicle" className="w-full" options={vehicles} optionLabel="name" optionValue="id" filter value={targetVehicle}
        disabled={busy} onChange={e => setTargetVehicle(e.value)} />
    </Dialog>
    <Dialog visible={swapOpen} header="Swap delivery assignments" modal style={{ width: "min(95vw, 520px)" }}
      closable={!busy} onHide={() => { if (!busy) setSwapOpen(false); }}
      footer={<Button label="Confirm swap" loading={busy} disabled={!canSwap || prepared.length !== 2} onClick={() => void swap()} />}>
      <p>Eligible open orders will exchange executives and scheduled dates. Locked, delivered and other closed orders stay in place. Incoming orders keep their vehicles and relative order, after retained orders.</p>
      {prepared.map((a, i) => <p key={`${a.executiveId}|${a.deliveryDate}`}>
        <strong>{orderCountLabel(a.swapAssignmentIds?.length ?? 0)} to swap; {orderCountLabel(a.assignments.length - (a.swapAssignmentIds?.length ?? 0))} staying</strong>
        {" — "}{selected[i]?.executiveName}, {dateLabel(a.deliveryDate)}</p>)}
    </Dialog>
  </div>;
};
export default DeliveryAssessmentPlanning;
